import React, { useState } from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useSubscriptionsStore } from '../../../store/models/subscriptions';
import ConfirmDialog from '../../components/confirmDialog';

function useSubscriptionDialogs({ unsubscribe, addSchedule, removeSchedule }) {
    const subscriptionsStore = useSubscriptionsStore();

    const [unsubscribeConfirmOpen, setUnsubscribeConfirmOpen] = useState(false);
    const [unsubscribeSuccessOpen, setUnsubscribeSuccessOpen] = useState(false);
    const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
    const [removeLoading, setRemoveLoading] = useState(false);
    const [removeSuccessOpen, setRemoveSuccessOpen] = useState(false);
    const [addSuccessOpen, setAddSuccessOpen] = useState(false);

    const [pendingSubscription, setPendingSubscription] = useState(null);
    const [pendingRemove, setPendingRemove] = useState({ subscriptionId: null, scheduleId: null, scheduleName: null });

    function openUnsubscribeDialog(subscription) {
        setPendingSubscription(subscription);
        setUnsubscribeConfirmOpen(true);
    }

    function openRemoveDialog(subscriptionId, scheduleId) {
        const schedules = subscriptionsStore.connectedSchedules[subscriptionId] || [];
        const schedule = schedules.find((s) => s.scheduleId === scheduleId || s.id === scheduleId);
        setPendingRemove({ subscriptionId, scheduleId, scheduleName: schedule?.scheduleName || schedule?.name });
        setRemoveConfirmOpen(true);
    }

    async function handleUnsubscribeConfirm() {
        setUnsubscribeConfirmOpen(false);
        subscriptionsStore.startSyncRefresh();
        let result;
        try {
            result = await unsubscribe(pendingSubscription);
        } finally {
            subscriptionsStore.stopSyncRefresh();
        }
        if (result?.success) {
            setUnsubscribeSuccessOpen(true);
        }
    }

    function handleUnsubscribeSuccessClose() {
        setUnsubscribeSuccessOpen(false);
        // unsubscribe() already moved the item from active to inactive locally
        // — no refetch needed, and one risked racing an in-flight Load More.
    }

    async function handleRemoveConfirm() {
        setRemoveLoading(true);
        let result;
        try {
            result = await removeSchedule(pendingRemove.subscriptionId, pendingRemove.scheduleId);
        } finally {
            setRemoveLoading(false);
        }
        if (result?.success) {
            setRemoveConfirmOpen(false);
            setRemoveSuccessOpen(true);
        }
    }

    function handleRemoveSuccessClose() {
        setRemoveSuccessOpen(false);
        // deleteSchedule() already removed it from connectedSchedules locally
        // — no refetch needed, and one risked racing an in-flight Load More.
    }

    // No confirm dialog to keep open here (add has no confirm step), so the
    // page-wide overlay is the loading indicator instead — the success
    // dialog itself only ever opens once the request has actually
    // succeeded, so it can never flash open and then vanish on a failure.
    async function handleAddSchedule(subscriptionId, scheduleId) {
        subscriptionsStore.startSyncRefresh();
        let result;
        try {
            result = await addSchedule(subscriptionId, scheduleId);
        } finally {
            subscriptionsStore.stopSyncRefresh();
        }
        if (result?.success) {
            setAddSuccessOpen(true);
        }
    }

    function handleAddSuccessClose() {
        setAddSuccessOpen(false);
        // addSchedule() already updated connectedSchedules/availableSchedules
        // locally — no refetch needed, and one risked racing an in-flight
        // Load More (same reasoning as unsubscribe/remove schedule).
    }

    function renderDialogs() {
        return (
            <React.Fragment>
                <ConfirmDialog
                    open={unsubscribeConfirmOpen}
                    variant="warn"
                    title="Are you sure?"
                    subMessage={`This will remove ${pendingSubscription?.publisher?.name || 'this publisher'} from your calendar.`}
                    confirmLabel="Yes, unsubscribe"
                    cancelLabel="Cancel"
                    onConfirm={handleUnsubscribeConfirm}
                    onClose={() => setUnsubscribeConfirmOpen(false)}
                />
                <ConfirmDialog
                    open={unsubscribeSuccessOpen}
                    variant="success"
                    title="Unsubscribed"
                    subMessage={`You have successfully unsubscribed from ${pendingSubscription?.publisher?.name || 'this publisher'}.`}
                    note="Please refresh your calendar to reveal the change."
                    NoteIcon={RefreshIcon}
                    onClose={handleUnsubscribeSuccessClose}
                />
                <ConfirmDialog
                    open={removeConfirmOpen}
                    variant="warn"
                    title="Are you sure?"
                    subMessage={`This will remove ${pendingRemove.scheduleName || 'this schedule'} from your calendar.`}
                    confirmLabel="Yes, remove"
                    cancelLabel="Cancel"
                    loading={removeLoading}
                    onConfirm={handleRemoveConfirm}
                    onClose={() => setRemoveConfirmOpen(false)}
                />
                <ConfirmDialog
                    open={removeSuccessOpen}
                    variant="success"
                    title="Removed"
                    subMessage={`You have successfully removed ${pendingRemove.scheduleName || 'this schedule'} from your calendar.`}
                    note="Please allow a moment for the magic to happen."
                    onClose={handleRemoveSuccessClose}
                />
                <ConfirmDialog
                    open={addSuccessOpen}
                    variant="success"
                    title="Schedule Added"
                    subMessage="Events will now appear in your calendar."
                    note="Please allow a moment for the magic to happen."
                    onClose={handleAddSuccessClose}
                />
            </React.Fragment>
        );
    }

    return {
        openUnsubscribeDialog,
        openRemoveDialog,
        handleAddSchedule,
        renderDialogs,
    };
}

export default useSubscriptionDialogs;
